<template>
  <div class="health-calendar">
    <!-- Month Navigation -->
    <div class="month-nav px-4 py-2">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-btn icon variant="text" color="grey-darken-1" @click="navigateWeek(-1)">
            <v-icon icon="fa-chevron-left"></v-icon>
          </v-btn>
          <span 
            class="text-grey-darken-1 text-body-1 font-weight-semibold month-selector"
            @click="showMonthPicker = true"
          >
            {{ currentMonth }} {{ currentYear }}
          </span>
          <v-btn icon variant="text" color="grey-darken-1" @click="navigateWeek(1)">
            <v-icon icon="fa-chevron-right"></v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Month Picker Dialog -->
    <v-dialog v-model="showMonthPicker" width="300">
      <v-card>
        <v-card-text class="pa-4">
          <div class="d-flex flex-column">
            <!-- Year Selector -->
            <v-select
              v-model="tempYear"
              :items="yearOptions"
              label="Year"
              variant="outlined"
              density="compact"
              class="mb-4"
            ></v-select>
            
            <!-- Month Grid -->
            <div class="month-grid">
              <v-btn
                v-for="(month, index) in monthNames"
                :key="month"
                variant="text"
                :color="tempMonth === index ? 'primary' : ''"
                @click="selectMonth(index)"
                class="month-btn"
              >
                {{ month.slice(0, 3) }}
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showMonthPicker = false">Cancel</v-btn>
          <v-btn color="primary" variant="text" @click="applyMonthYear">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Calendar Days -->
    <div class="calendar-days px-4 py-2">
      <div class="d-flex justify-space-between">
        <div v-for="day in ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']" :key="day"
          class="day-label text-caption font-weight-medium"
          :class="{'grey-text': day === 'SAT' || day === 'SUN'}">
          {{ day }}
        </div>
      </div>
      
      <!-- Calendar Dates -->
      <div class="d-flex justify-space-between mt-4">
        <div v-for="date in weekDates" :key="date.getTime()"
          class="date-number text-subtitle-1 font-weight-medium"
          :class="{
            'active': isSelectedDate(date)
          }"
          @click="selectDate(date)">
          {{ date.getDate() }}
        </div>
      </div>
    </div>

    <!-- Selected Date -->
    <div class="selected-date text-center py-2">
      <div class="text-caption text-grey-darken-1">{{ formattedSelectedDate }}</div>
    </div>

    <!-- Entries List -->
    <div class="entries-list px-4 pt-4">
      <v-alert
        v-if="error"
        type="error"
        class="mb-4"
      >
        {{ error }}
      </v-alert>

      <v-progress-circular
        v-if="isLoading"
        indeterminate
        color="primary"
        class="ma-4"
      ></v-progress-circular>

      <template v-else>
        <v-card v-if="!entries || entries.length === 0" class="mb-4">
          <v-card-text class="text-center pa-4">
            No diary or planner entries for this date. Click "New Entry" to add a diary entry.
          </v-card-text>
        </v-card>

        <v-card v-else v-for="entry in entries" :key="entry.source === 'planner' && entry.is_virtual_occurrence ? `${entry.original_entry_id}-${entry.time}` : entry.id"
          :color="entry.color || 'white'"
          class="mb-4 entry-card"
          :class="{'white-text': entry.whiteText}"
          elevation="1"
          rounded="lg"
          @click="showEntryDetails(entry)">
          
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <template v-if="entry.entry_type === 'drank'">
                <DrinkIcon :color="entry.iconColor" class="me-4" />
              </template>
              <template v-else-if="entry.entry_type === 'felt'">
                <FeelIcon :color="entry.iconColor" class="me-4" />
              </template>
              <template v-else-if="entry.entry_type === 'did'">
                <DidIcon :color="entry.iconColor" class="me-4" />
              </template>
              <template v-else-if="entry.entry_type === 'exercised'">
                <ExerciseIcon :color="entry.iconColor" class="me-4" />
              </template>
              <v-icon v-else :icon="entry.icon" size="24" :color="entry.iconColor" class="me-4"></v-icon>
              <div class="flex-grow-1">
                <div class="text-body-2 font-weight-semibold" :style="entry.whiteText ? 'color: white' : ''">{{ entry.time }}</div>
                
                <!-- Diary Entry Content -->
                <template v-if="entry.source === 'diary' && entry.ate_content">
                  <div class="d-flex align-center mt-1">
                    <span v-if="entry.entry_type !== 'event'" :style="`color: ${settingsStore.settings.buttonColor}`" class="font-weight-bold">{{ getEntryTypeLabel(entry.entry_type) }} </span>
                    <span class="ml-1 truncate">{{ entry.ate_content }}</span>
                  </div>
                </template>
                
                <!-- Planner Entry Content -->
                <template v-if="entry.source === 'planner'">
                  <div class="d-flex align-center mt-1">
                    <span :style="`color: white`" class="font-weight-bold">{{ entry.planner_type }}: </span>
                    <span class="ml-1 truncate" :style="`color: white`">{{ entry.name }}</span>
                    <span v-if="entry.is_recurring" class="ml-2" :style="`color: white`">(Recurring)</span>
                  </div>
                </template>

              </div>
              <v-spacer></v-spacer>
              <v-chip v-if="entry.status"
                :color="entry.source === 'planner' ? 'white' : entry.status.color"
                :text-color="entry.source === 'planner' ? entry.status.color : entry.status.textColor"
                size="small"
                class="status-chip"
                :class="{ 'clickable-status': entry.source === 'planner' }"
                @click.stop="entry.source === 'planner' ? togglePlannerStatus(entry) : null">
                {{ entry.status.text }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </div>

    <!-- Add Entry Button -->
    <ButtonFixed
      text="New Entry"
      custom-class="circular-add-button text-white primary-button"
      @click="showEntryTypeSelector = true"
    />

    <!-- Entry Type Selector Bottom Sheet -->
    <v-bottom-sheet 
      v-model="showEntryTypeSelector"
      :scrim="true"
      class="entry-type-bottom-sheet"
      width="500"
      location="bottom"
    >
      <v-card class="entry-type-card">
        <!-- Handle bar -->
        <div class="handle-bar"></div>
        
        <v-card-text class="pa-6">
          <div class="entry-type-grid">
            <v-card
              v-for="entryType in entryTypes"
              :key="entryType.type"
              class="entry-type-button"
              elevation="0"
              :ripple="true"
              :style="{ backgroundColor: lightButtonColor, border: `1px solid ${settingsStore.settings.buttonColor}` }"
              @click="selectEntryType(entryType.type)"
            >
              <v-card-text class="d-flex align-center pa-4">
                <template v-if="entryType.type === 'drank'">
                  <DrinkIcon :color="settingsStore.settings.buttonColor" class="me-2" />
                </template>
                <template v-else-if="entryType.type === 'felt'">
                  <FeelIcon :color="settingsStore.settings.buttonColor" class="me-2" />
                </template>
                <template v-else-if="entryType.type === 'did'">
                  <DidIcon :color="settingsStore.settings.buttonColor" class="me-2" />
                </template>
                <template v-else-if="entryType.type === 'exercised'">
                  <ExerciseIcon :color="settingsStore.settings.buttonColor" class="me-2" />
                </template>
                <v-icon v-else :icon="entryType.icon" size="20" :color="settingsStore.settings.buttonColor" class="me-2"></v-icon>
                <span class="entry-type-text" :style="{ color: settingsStore.settings.buttonColor }">{{ entryType.label }}...</span>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>

    <!-- Entry Details Popup -->
    <DiaryEntryForm
      v-model="showEntryDetailsPopup"
      :entry-type="selectedEntry?.entry_type"
      :existing-entry="selectedEntry"
      :is-editing="true"
      @entry-updated="handleEntryUpdated"
    />

    <!-- Planner Entry Details Popup -->
    <PlannerEntryForm
      v-model="showPlannerDetailsPopup"
      :planner-entry="selectedPlannerEntry"
      @status-updated="handlePlannerStatusUpdated"
    />

    <!-- Diary Entry Form Dialog -->
    <DiaryEntryForm
      v-model="showDiaryEntryForm"
      :entry-type="selectedEntryType"
      @entry-created="handleEntryCreated"
    />

    <!-- Event Booking Details Modal -->
    <v-dialog v-model="showEventBookingPopup" max-width="500px">
      <v-card class="event-booking-card">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon icon="fa fa-calendar" size="24" :color="settingsStore.settings.buttonColor2" class="me-3"></v-icon>
          Event Booking Details
        </v-card-title>
        
        <v-card-text class="pa-6">
          <div v-if="selectedEventEntry">
            <!-- Event Time -->
            <div class="mb-4">
              <div class="text-subtitle-2 font-weight-semibold mb-1" :style="`color: ${settingsStore.settings.buttonColor2}`">
                Time
              </div>
              <div class="text-body-1">{{ selectedEventEntry.time }}</div>
            </div>
            
            <!-- Event Details -->
            <div class="mb-4">
              <div class="text-subtitle-2 font-weight-semibold mb-1" :style="`color: ${settingsStore.settings.buttonColor2}`">
                Event Details
              </div>
              <div class="text-body-1">{{ selectedEventEntry.ate_content || selectedEventEntry.description }}</div>
            </div>
            
            <!-- Event Date -->
            <div class="mb-4">
              <div class="text-subtitle-2 font-weight-semibold mb-1" :style="`color: ${settingsStore.settings.buttonColor2}`">
                Date
              </div>
              <div class="text-body-1">{{ formattedSelectedDate }}</div>
            </div>
            
            <!-- Additional Notes (if any) -->
            <div v-if="selectedEventEntry.noticed_content" class="mb-4">
              <div class="text-subtitle-2 font-weight-semibold mb-1" :style="`color: ${settingsStore.settings.buttonColor2}`">
                Notes
              </div>
              <div class="text-body-1">{{ selectedEventEntry.noticed_content }}</div>
            </div>
            
            <!-- Event Image (if any) -->
            <div v-if="selectedEventEntry.image_url" class="mb-4">
              <div class="text-subtitle-2 font-weight-semibold mb-1" :style="`color: ${settingsStore.settings.buttonColor2}`">
                Image
              </div>
              <img 
                :src="selectedEventEntry.image_url" 
                alt="Event image"
                class="event-image"
              />
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            variant="text" 
            @click="showEventBookingPopup = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject } from 'vue';
import ButtonFixed from "@/components/custom/ButtonFixed.vue";
import DiaryEntryForm from "@/components/HealthSuite/DiaryEntryForm.vue";
import PlannerEntryForm from "@/components/HealthSuite/PlannerEntryForm.vue";
import DrinkIcon from "@/components/Icons/DrinkIcon.vue";
import FeelIcon from "@/components/Icons/FeelIcon.vue";
import DidIcon from "@/components/Icons/DidIcon.vue";
import ExerciseIcon from "@/components/Icons/ExerciseIcon.vue";
import { useSettingsStore } from "@/api/store/settings";
import { healthDiaryService, type DiaryEntry } from "@/api/services/healthDiary";
import { healthPlannerService, type PlannerEntry } from "@/api/services/healthPlanner";
import { hexToRgba } from "@/tools/design";

interface CalendarEntry {
  id: number;
  time: string;
  description: string;
  icon: string;
  iconColor: string;
  color?: string;
  whiteText?: boolean;
  entry_type?: string;
  ate_content?: string;
  noticed_content?: string;
  practitioner_comment?: string;
  status?: {
    text: string;
    color: string;
    textColor: string;
  };
  image_url?: string;
  // Planner entry specific fields
  name?: string;
  planner_type?: string; // Exercise, Food, Drink, Lifestyle
  planner_status?: 'pending' | 'completed' | 'ignored';
  source: 'diary' | 'planner'; // To distinguish entry types
  is_recurring?: boolean;
  is_virtual_occurrence?: boolean;
  original_entry_id?: number;
}

const settingsStore = useSettingsStore();
const showDiaryEntryForm = ref(false);
const showEntryTypeSelector = ref(false);
const selectedEntryType = ref<string | null>(null);

// Date management
const currentDate = new Date();
const selectedDate = ref(new Date());
const weekStartDate = ref(new Date());

// Set initial week start to Monday of current week
const dayOfWeek = currentDate.getDay();
const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
weekStartDate.value.setDate(currentDate.getDate() - daysToSubtract);

// Add refresh calendar functionality
const resetToCurrentDate = () => {
  const today = new Date();
  selectedDate.value = today;
  const mondayOfCurrentWeek = new Date(today);
  const dayOfWeek = today.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  mondayOfCurrentWeek.setDate(today.getDate() - daysToSubtract);
  weekStartDate.value = mondayOfCurrentWeek;
  fetchEntries();
};

type RefreshFunction = () => void;
const refreshCalendarRef = inject('refreshCalendar', ref<RefreshFunction>(() => { /* intentionally empty */ }));
refreshCalendarRef.value = resetToCurrentDate;

// Computed properties for date display
const weekDates = computed(() => {
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStartDate.value);
    date.setDate(weekStartDate.value.getDate() + i);
    dates.push(date);
  }
  return dates;
});

const currentMonth = computed(() => {
  return weekStartDate.value.toLocaleString('default', { month: 'long' });
});

const currentYear = computed(() => {
  return weekStartDate.value.getFullYear();
});

const formattedSelectedDate = computed(() => {
  return selectedDate.value.toLocaleDateString('default', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    weekday: 'long'
  });
});

// Date helper functions
const isCurrentDate = (date: Date) => {
  return date.toDateString() === currentDate.toDateString();
};

const isSelectedDate = (date: Date) => {
  return date.toDateString() === selectedDate.value.toDateString();
};

const selectDate = (date: Date) => {
  selectedDate.value = new Date(date);
};

const navigateWeek = (direction: number) => {
  const newDate = new Date(weekStartDate.value);
  newDate.setDate(weekStartDate.value.getDate() + (direction * 7));
  weekStartDate.value = newDate;
};

const entries = ref<CalendarEntry[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

// Helper function to get entry type icon
const getEntryTypeIcon = (entryType?: string) => {
  switch (entryType) {
    case 'ate':
      return 'fa fa-cutlery';
    case 'drank':
      return 'custom-drink';
    case 'slept':
      return 'fa fa-bed';
    case 'event':
      return 'fa fa-calendar';
    default:
      return 'fa fa-cutlery';
  }
};

// Helper function to get planner type icon
const getPlannerTypeIcon = (plannerType?: string) => {
  switch (plannerType) {
    case 'Exercise':
      return 'fa fa-star';
    case 'Food':
      return 'fa fa-cutlery';
    case 'Drink':
      return 'fa fa-glass';
    case 'Lifestyle':
      return 'fa fa-heart';
    default:
      return 'fa fa-calendar';
  }
};

// Helper function to get planner status info
const getPlannerStatusInfo = (status?: string) => {
  switch (status) {
    case 'completed':
      return {
        text: 'Completed',
        color: '#4CAF50',
        textColor: 'white'
      };
    case 'ignored':
      return {
        text: 'Ignored',
        color: '#9E9E9E',
        textColor: 'white'
      };
    case 'pending':
    default:
      return {
        text: 'Pending',
        color: '#FF9800',
        textColor: 'white'
      };
  }
};

// Helper function to get entry type label
const getEntryTypeLabel = (entryType?: string) => {
  switch (entryType) {
    case 'ate':
      return 'I ate';
    case 'drank':
      return 'I drank';
    case 'exercised':
      return 'I exercised';
    case 'did':
      return 'I did';
    case 'slept':
      return 'I slept';
    case 'felt':
      return 'I feel';
    case 'event':
      return 'Event';
    default:
      return 'I ate';
  }
};

const fetchEntries = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const dateString = selectedDate.value.toISOString().split('T')[0];
    
    // Fetch both diary and planner entries concurrently
    const [diaryEntries, plannerEntries] = await Promise.all([
      healthDiaryService.indexWithEvents(dateString),
      healthPlannerService.index(dateString)
    ]);

    // Transform diary entries
    const transformedDiaryEntries: CalendarEntry[] = Array.isArray(diaryEntries) ? diaryEntries
      .filter((entry): entry is DiaryEntry & { id: number } => entry && entry.id !== undefined)
      .map((entry) => ({
        id: entry.id,
        time: new Date(entry.entry_datetime).toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' }),
        entry_type: entry.entry_type,
        ate_content: entry.ate_content,
        noticed_content: entry.noticed_content,
        practitioner_comment: entry.practitioner_comment,
        description: entry.ate_content || entry.noticed_content || '',
        icon: getEntryTypeIcon(entry.entry_type),
        iconColor: entry.entry_type === 'event' ? settingsStore.settings.buttonColor2 : settingsStore.settings.buttonColor,
        color: 'white',
        image_url: entry.file?.file_path || undefined,
        source: 'diary' as const
      })) : [];

    // Transform planner entries
    const transformedPlannerEntries: CalendarEntry[] = Array.isArray(plannerEntries) ? plannerEntries
      .filter(entry => entry && (entry.id !== undefined || entry.original_entry_id !== undefined))
      .map((entry) => {
        const entryTime = new Date(entry.scheduled_datetime);
        return {
          id: entry.id || entry.original_entry_id, // Use original_entry_id as fallback
          time: entryTime.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' }),
          name: entry.name,
          planner_type: entry.type,
          planner_status: entry.status,
          description: entry.description || entry.name || '',
          icon: getPlannerTypeIcon(entry.type),
          iconColor: 'white',
          color: entry.status === 'completed' ? hexToRgba(settingsStore.settings.primaryColor, 0.6) : 
                 entry.status === 'ignored' ? hexToRgba(settingsStore.settings.primaryColor, 0.4) : 
                 settingsStore.settings.primaryColor,
          whiteText: true,
          status: getPlannerStatusInfo(entry.status),
          image_url: entry.file?.file_path || undefined,
          source: 'planner' as const,
          is_recurring: entry.is_recurring,
          is_virtual_occurrence: entry.is_virtual_occurrence,
          original_entry_id: entry.original_entry_id
        };
      }) : [];

    // Combine and sort entries by time
    const allEntries = [...transformedDiaryEntries, ...transformedPlannerEntries].sort((a, b) => {
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });

    entries.value = allEntries;
  } catch (err: any) {
    console.error('Error fetching entries:', err);
    error.value = 'Failed to load entries. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchEntries();
});

watch(selectedDate, () => {
  fetchEntries();
});

const showMonthPicker = ref(false);
const tempYear = ref(currentYear.value);
const tempMonth = ref(currentDate.getMonth());
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Generate a list of years (5 years before and after current year)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    years.push(i);
  }
  return years;
});

const selectMonth = (index: number) => {
  tempMonth.value = index;
};

const applyMonthYear = () => {
  const newDate = new Date(tempYear.value, tempMonth.value, 1);
  // Adjust to start of week
  const dayOfWeek = newDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  newDate.setDate(newDate.getDate() - daysToSubtract);
  weekStartDate.value = newDate;
  showMonthPicker.value = false;
};

// Entry types for the bottom sheet
const entryTypes = [
  { type: 'ate', label: 'I ate', icon: 'fa fa-cutlery' },
  { type: 'drank', label: 'I drank', icon: 'fa fa-glass' },
  { type: 'exercised', label: 'I exercised', icon: 'fa fa-star' },
  { type: 'did', label: 'I did...', icon: 'fa fa-plus' },
  { type: 'slept', label: 'I slept...', icon: 'fa fa-bed' },
  { type: 'felt', label: 'I feel...', icon: 'fa fa-heart' },
  { type: 'event', label: 'Event...', icon: 'fa fa-calendar' }
];

const selectEntryType = (type: string) => {
  selectedEntryType.value = type;
  showEntryTypeSelector.value = false;
  showDiaryEntryForm.value = true;
};



// Computed colors for button styling
const lightButtonColor = computed(() => {
  return hexToRgba(settingsStore.settings.buttonColor, 0.15);
});

const borderButtonColor = computed(() => {
  return hexToRgba(settingsStore.settings.buttonColor, 0.3);
});

const showEntryDetailsPopup = ref(false);
const selectedEntry = ref<DiaryEntry | null>(null);
const showPlannerDetailsPopup = ref(false);
const selectedPlannerEntry = ref<PlannerEntry | null>(null);
const showEventBookingPopup = ref(false);
const selectedEventEntry = ref<CalendarEntry | null>(null);

const showEntryDetails = async (entry: CalendarEntry) => {
  if (entry.source === 'diary') {
    // Check if it's an event entry - show event booking details instead of diary form
    if (entry.entry_type === 'event') {
      selectedEventEntry.value = entry;
      showEventBookingPopup.value = true;
      return;
    }
    
    // Show regular diary entry details
    try {
      const fullEntry = await healthDiaryService.show(entry.id);
      selectedEntry.value = fullEntry;
      showEntryDetailsPopup.value = true;
    } catch (error) {
      console.error('Error fetching entry details:', error);
      // Fallback to the original method if fetch fails
      selectedEntry.value = {
        id: entry.id,
        entry_datetime: new Date(selectedDate.value.toDateString() + ' ' + entry.time).toISOString(),
        entry_type: entry.entry_type,
        ate_content: entry.ate_content,
        noticed_content: entry.noticed_content,
        practitioner_comment: entry.practitioner_comment,
        read_by_practitioner: false
      };
      showEntryDetailsPopup.value = true;
    }
  } else if (entry.source === 'planner') {
    // Show planner entry details
    try {
      // If it's a virtual occurrence, fetch the original entry
      const entryId = entry.is_virtual_occurrence ? entry.original_entry_id : entry.id;
      if (!entryId) {
        throw new Error('No entry ID available');
      }
      const fullEntry = await healthPlannerService.show(entryId);
      selectedPlannerEntry.value = {
        ...fullEntry,
        scheduled_datetime: new Date(selectedDate.value.toDateString() + ' ' + entry.time).toISOString()
      };
      showPlannerDetailsPopup.value = true;
    } catch (error) {
      console.error('Error fetching planner entry details:', error);
    }
  }
};

const togglePlannerStatus = async (entry: CalendarEntry) => {
  if (entry.source !== 'planner' || !entry.planner_status) return;
  
  try {
    let newStatus: 'pending' | 'completed' | 'ignored';
    
    // Cycle through statuses: pending -> completed -> ignored -> pending
    switch (entry.planner_status) {
      case 'pending':
        newStatus = 'completed';
        break;
      case 'completed':
        newStatus = 'ignored';
        break;
      case 'ignored':
        newStatus = 'pending';
        break;
      default:
        newStatus = 'pending';
    }
    
    // Use original entry ID for virtual occurrences
    const entryId = entry.is_virtual_occurrence ? entry.original_entry_id : entry.id;
    if (!entryId) {
      throw new Error('No entry ID available');
    }
    
    // For virtual occurrences, construct the scheduled_datetime from the selected date and time
    let scheduledDatetime: string | undefined;
    if (entry.is_virtual_occurrence) {
      scheduledDatetime = new Date(selectedDate.value.toDateString() + ' ' + entry.time).toISOString();
    }
    
    await healthPlannerService.updateStatus(entryId, newStatus, scheduledDatetime);
    // Refresh entries to reflect the change
    fetchEntries();
  } catch (error) {
    console.error('Error updating planner entry status:', error);
  }
};

const handleEntryUpdated = (entryDateTime: string) => {
  const entryDate = new Date(entryDateTime);
  const entryDateString = entryDate.toDateString();
  const currentSelectedDateString = selectedDate.value.toDateString();
  
  // If the entry date is different from the currently selected date, navigate to it
  if (entryDateString !== currentSelectedDateString) {
    // Update the selected date
    selectedDate.value = new Date(entryDate);
    
    // Calculate the start of the week for the new date (Monday)
    const dayOfWeek = entryDate.getDay();
    const mondayOfWeek = new Date(entryDate);
    mondayOfWeek.setDate(entryDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    
    // Update the week start date to show the correct week
    weekStartDate.value = mondayOfWeek;
  }
  
  // Fetch entries for the (potentially new) selected date
  fetchEntries();
};

const handleEntryCreated = (entryDateTime: string) => {
  const entryDate = new Date(entryDateTime);
  const entryDateString = entryDate.toDateString();
  const currentSelectedDateString = selectedDate.value.toDateString();
  
  // If the entry date is different from the currently selected date, navigate to it
  if (entryDateString !== currentSelectedDateString) {
    // Update the selected date
    selectedDate.value = new Date(entryDate);
    
    // Calculate the start of the week for the new date (Monday)
    const dayOfWeek = entryDate.getDay();
    const mondayOfWeek = new Date(entryDate);
    mondayOfWeek.setDate(entryDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    
    // Update the week start date to show the correct week
    weekStartDate.value = mondayOfWeek;
  }
  
  // Fetch entries for the (potentially new) selected date
  fetchEntries();
};

const handlePlannerStatusUpdated = () => {
  // Refresh entries to reflect the status change
  fetchEntries();
};
</script>

<style scoped>
.health-calendar {
  background-color: #F5F5F5;
  min-height: calc(100vh - 82px); /* Adjust for the top bar */
  padding-top: 0;
  padding-bottom: 80px; /* Increase bottom padding */
}

.month-nav {
  border-bottom: 1px solid rgba(30, 188, 213, 0.2);
}

.day-label {
  width: 36px;
  text-align: center;
}

.date-number {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.date-number:hover {
  background-color: rgba(124, 230, 148, 0.1);
}

.date-number.active {
  background-color: #7CE694;
  color: white;
}

.date-number.selected:not(.active) {
  background-color: rgba(124, 230, 148, 0.2);
}

.grey-text {
  color: #7F898E;
}

.entry-card {
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.entry-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.white-text {
  color: white;
}

.status-chip {
  font-size: 8px;
  height: 15px !important;
}

.time-indicator {
  margin-bottom: 0;
  padding-bottom: 0;
}

.add-entry-btn {
  border-radius: 20px;
  color: white;
  text-transform: none;
  font-size: 12px;
  font-weight: 600;
}

.month-selector {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.month-selector:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.month-btn {
  text-transform: none !important;
  letter-spacing: normal !important;
}

.circular-add-button {
  border-radius: 50% !important;
  width: 56px !important;
  height: 56px !important;
  min-width: 56px !important;
  font-size: 24px !important;
  margin: 0 auto !important;
}

:deep(.add-new-button-container) {
  padding: 16px !important;
  margin-bottom: 16px !important;
}

:deep(.position-relative) {
  display: flex !important;
  justify-content: center !important;
}

:deep(.add-new-circle-icon) {
  margin-right: 3px;
}

/* Entry Type Selector Bottom Sheet */
:deep(.entry-type-bottom-sheet) {
  max-width: 600px !important;
  margin: 0 auto !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
}

:deep(.entry-type-bottom-sheet .v-overlay__content) {
  align-items: flex-end !important;
  width: 100% !important;
}

:deep(.entry-type-bottom-sheet .v-overlay__scrim) {
  position: fixed !important;
  left: 0 !important;
  transform: none !important;
  width: 100vw !important;
}

:deep(.entry-type-bottom-sheet .v-bottom-sheet__activator) {
  position: relative;
}

.entry-type-card {
  background: #FFFFFF;
  box-shadow: 0.5px -3px 15px rgba(0, 0, 0, 0.05);
  border-radius: 20px 20px 0px 0px;
  width: 100%;
  min-height: 287px;
  margin: 0;
  position: relative;
}

.handle-bar {
  width: 32px;
  height: 4px;
  background: #E0E0E0;
  border-radius: 2px;
  margin: 12px auto 8px auto;
}

.entry-type-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
}

.entry-type-button {
  box-sizing: border-box;
  box-shadow: 0.5px 0.5px 15px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  height: 60px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.entry-type-button:hover {
  transform: translateY(-1px);
  box-shadow: 0.5px 0.5px 20px rgba(0, 0, 0, 0.1);
}

.entry-type-button .v-card-text {
  height: 100%;
  padding: 0 16px !important;
}

.entry-type-text {
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.entry-details-popup {
  border-radius: 16px;
}

:deep(.entry-details-popup .v-field) {
  border-radius: 12px !important;
}

:deep(.entry-details-popup .v-textarea .v-field__input) {
  min-height: 120px !important;
  padding-top: 12px;
}

:deep(.entry-details-popup .v-label) {
  font-size: 14px;
  font-weight: 500;
}

:deep(.entry-details-popup .v-btn) {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
}

.invisible {
  visibility: hidden;
}

.entry-thumbnail {
  max-width: 150px;
  max-height: 100px;
  width: auto;
  height: auto;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #E5E7EB;
}

.clickable-status {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.clickable-status:hover {
  opacity: 0.8;
}

/* Event Booking Modal Styles */
.event-booking-card {
  border-radius: 16px;
}

:deep(.event-booking-card .v-card-title) {
  border-bottom: 1px solid #E5E7EB;
  padding-bottom: 12px;
}

.event-image {
  max-width: 100%;
  max-height: 200px;
  width: auto;
  height: auto;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
}

:deep(.event-booking-card .v-btn) {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
}
</style> 